import React, { useEffect, useRef, useState } from "react";
import { api } from "../api";
import {
  Box, Button, Typography, ImageList, ImageListItem, ImageListItemBar, IconButton,
  LinearProgress, List, ListItem, ListItemIcon, ListItemText, Chip,
} from "@mui/material";
import { mediaThumb } from "../../services/media";

const UPLOAD_URL = "https://media.swancreate.com/upload";
const MEDIA_PUBLIC_BASE = "https://media.swancreate.com/images/gallery/";
const MAX_BYTES = 50 * 1024 * 1024;

function parseUploadUrl(json) {
  if (json && typeof json.permalink === "string") return json.permalink;
  if (json && typeof json.url === "string") return json.url;
  const m = json && typeof json.message === "string" && json.message.match(/'([^']+)'/);
  return m ? `${MEDIA_PUBLIC_BASE}${m[1]}` : null;
}

// Upload one file to the media server. Returns the URL, or throws an Error with a clear message.
async function uploadToMedia(file) {
  const fd = new FormData();
  fd.append("file", file, file.name);
  fd.append("folder", "gallery");
  let res;
  try {
    res = await fetch(UPLOAD_URL, { method: "POST", body: fd });
  } catch (e) {
    throw new Error("Could not reach the media server (network/connection error)");
  }
  if (!res.ok) {
    let detail = "";
    try { detail = (await res.text()).slice(0, 160); } catch (_) { /* ignore */ }
    throw new Error(`Media server rejected the file (HTTP ${res.status})${detail ? ": " + detail : ""}`);
  }
  let json;
  try { json = await res.json(); } catch (e) { throw new Error("Media server returned an unreadable response"); }
  const url = parseUploadUrl(json);
  if (!url) throw new Error("Upload succeeded but no file URL was returned");
  return url;
}

export default function ImageUploader({ galleryId, onCover }) {
  const [images, setImages] = useState([]);
  const [items, setItems] = useState([]); // { name, status: queued|uploading|done|error, error }
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const inputRef = useRef(null);

  const load = () =>
    api.get(`/sk12_galleryimages?GalleryId=${galleryId}`).then((r) => setImages(r.data || []));
  useEffect(() => { load(); }, [galleryId]);

  const setItem = (i, patch) =>
    setItems((prev) => prev.map((it, j) => (j === i ? { ...it, ...patch } : it)));
  const bump = () => setProgress((p) => ({ ...p, done: p.done + 1 }));

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    setItems(files.map((f) => ({ name: f.name, status: "queued", error: null })));
    setProgress({ done: 0, total: files.length });

    let order = images.reduce((m, i) => Math.max(m, (i.SortOrder ?? 0) + 1), images.length);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setItem(i, { status: "uploading" });

      if (!file.type.startsWith("image/")) {
        setItem(i, { status: "error", error: `Not an image file (${file.type || "unknown type"})` });
        bump(); continue;
      }
      if (file.size > MAX_BYTES) {
        setItem(i, { status: "error", error: `Too large — ${(file.size / 1048576).toFixed(1)} MB (max 50 MB)` });
        bump(); continue;
      }

      // Upload with one automatic retry on failure.
      let url = null, lastErr = null;
      for (let attempt = 0; attempt < 2 && !url; attempt++) {
        try { url = await uploadToMedia(file); }
        catch (err) { lastErr = err; }
      }
      if (!url) {
        setItem(i, { status: "error", error: lastErr ? lastErr.message : "Upload failed" });
        bump(); continue;
      }

      try {
        await api.post("/sk12_galleryimages", {
          GalleryId: galleryId, ImageUrl: url, Caption: file.name, SortOrder: order++,
        });
        setItem(i, { status: "done" });
      } catch (err) {
        setItem(i, { status: "error", error: "Uploaded to media, but saving to the gallery failed: " + (err?.response?.data?.error || err?.message || "database error") });
      }
      bump();
    }

    await load();
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = async (apiid) => { await api.delete(`/sk12_galleryimages/${apiid}`); await load(); };

  const okCount = items.filter((it) => it.status === "done").length;
  const errCount = items.filter((it) => it.status === "error").length;
  const icon = (s) =>
    s === "done" ? <span style={{ color: "#2e7d32" }}>✓</span>
      : s === "error" ? <span style={{ color: "#d32f2f" }}>✗</span>
      : s === "uploading" ? <span style={{ color: "#1976d2" }}>⤴</span>
      : <span style={{ color: "#999" }}>•</span>;

  return (
    <Box>
      <input ref={inputRef} type="file" accept="image/*" multiple hidden onChange={onPick} />
      <Button variant="contained" disabled={busy} onClick={() => inputRef.current && inputRef.current.click()}>
        {busy ? `Uploading ${progress.done}/${progress.total}…` : "Add Photos"}
      </Button>
      <Typography variant="caption" sx={{ ml: 2, color: "text.secondary" }}>
        Select multiple at once · {images.length} photo{images.length === 1 ? "" : "s"} in this gallery
      </Typography>
      {busy && (
        <LinearProgress
          variant={progress.total ? "determinate" : "indeterminate"}
          value={progress.total ? (progress.done / progress.total) * 100 : 0}
          sx={{ mt: 1 }}
        />
      )}

      {items.length > 0 && (
        <Box sx={{ mt: 2, border: "1px solid #e0e0e0", borderRadius: 1, maxHeight: 280, overflow: "auto" }}>
          <Box sx={{ px: 2, py: 1, bgcolor: "#fafafa", display: "flex", alignItems: "center", gap: 1, position: "sticky", top: 0 }}>
            <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
              {busy ? `Uploading ${progress.done} of ${progress.total}…` : "Upload results"}
            </Typography>
            {!busy && okCount > 0 && <Chip size="small" color="success" label={`${okCount} added`} />}
            {!busy && errCount > 0 && <Chip size="small" color="error" label={`${errCount} failed`} />}
          </Box>
          <List dense disablePadding>
            {items.map((it, i) => (
              <ListItem key={i} divider sx={{ py: 0.25 }}>
                <ListItemIcon sx={{ minWidth: 28 }}>{icon(it.status)}</ListItemIcon>
                <ListItemText
                  primary={it.name}
                  secondary={
                    it.status === "error" ? it.error
                      : it.status === "uploading" ? "uploading…"
                      : it.status === "done" ? "added" : "waiting…"
                  }
                  primaryTypographyProps={{ variant: "body2", noWrap: true }}
                  secondaryTypographyProps={{ color: it.status === "error" ? "error" : "text.secondary", variant: "caption" }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      <ImageList cols={4} gap={8} sx={{ mt: 2 }}>
        {images.map((img) => (
          <ImageListItem key={img.apiid}>
            <img src={mediaThumb(img.ImageUrl, 300)} alt={img.Caption || ""} loading="lazy" style={{ height: 160, objectFit: "cover" }} />
            <ImageListItemBar
              title={img.Caption || ""}
              actionIcon={
                <Box sx={{ display: "flex" }}>
                  <IconButton sx={{ color: "white" }} onClick={() => onCover && onCover(img.ImageUrl)} title="Set as cover">★</IconButton>
                  <IconButton sx={{ color: "white" }} onClick={() => remove(img.apiid)} title="Delete">✕</IconButton>
                </Box>
              }
            />
          </ImageListItem>
        ))}
      </ImageList>
    </Box>
  );
}
